import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { formatBoardDate, formatDate } from '@/lib/format';
import { addDays, toIsoDate, type CivilDate } from '@/lib/timezone';
import { isSameWeek } from '@/lib/dispatchBoard';
import type { BoardView } from './ViewToggle';

/**
 * Which day (or week) the board is showing, and how to move between them
 * (M1-09, extended by M1-10).
 *
 * The date is a real `<input type="date">` rather than a bespoke calendar
 * popover: it is keyboard-operable, localised, and screen-reader-labelled by
 * the platform, none of which a hand-rolled one gets for free.
 *
 * In week view the arrows step seven days rather than one, and the heading
 * names the range instead of a single date — but the underlying value is
 * still one date, the same one the day view uses. That is what lets the
 * toggle preserve it: switching views does not translate between two
 * different notions of "where the board is", because there is only one.
 */
export function BoardDateNav({
  date,
  dayStart,
  weekFirstDayStart,
  weekLastDayStart,
  view,
  timeZone,
  today,
  onChange,
}: {
  date: CivilDate;
  /** The instant the selected day begins, on the account's clock. */
  dayStart: Date;
  /**
   * The instants its week's first and last days begin. Both are passed rather
   * than derived from `dayStart`, because the selected date is usually in the
   * MIDDLE of the week being shown — a heading built from `dayStart` reads
   * "September 15 – September 19" for the week that starts on the 13th.
   */
  weekFirstDayStart: Date;
  weekLastDayStart: Date;
  view: BoardView;
  timeZone: string;
  today: CivilDate;
  onChange: (next: CivilDate) => void;
}) {
  const { t } = useTranslation();
  const step = view === 'week' ? 7 : 1;
  // In week view "today" means the week containing today, not that exact date:
  // the button is disabled while looking at this week, whichever of its seven
  // days the URL happens to name. Compared through `isSameWeek` so the
  // week-start rule lives in one place, and on CIVIL dates rather than on the
  // midnight instants -- reading a weekday off an instant is off by one in any
  // zone whose local midnight lands on a different UTC date.
  const isAtToday = view === 'week' ? isSameWeek(date, today) : toIsoDate(date) === toIsoDate(today);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          aria-label={view === 'week' ? t('dispatch.previousWeek') : t('dispatch.previousDay')}
          onClick={() => onChange(addDays(date, -step))}
        >
          <span aria-hidden="true">←</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          aria-label={view === 'week' ? t('dispatch.nextWeek') : t('dispatch.nextDay')}
          onClick={() => onChange(addDays(date, step))}
        >
          <span aria-hidden="true">→</span>
        </Button>
      </div>

      <Button variant="outline" size="sm" disabled={isAtToday} onClick={() => onChange(today)}>
        {view === 'week' ? t('dispatch.thisWeek') : t('dispatch.today')}
      </Button>

      <label className="flex items-center gap-2 text-sm">
        <span className="sr-only">{t('dispatch.dateLabel')}</span>
        <input
          type="date"
          className="h-8 rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          value={toIsoDate(date)}
          onChange={(event) => {
            // An empty or half-typed value is not a date change — the input
            // fires on every keystroke, and clearing it would otherwise throw
            // the board to the epoch mid-edit.
            const [year, month, day] = event.target.value.split('-').map(Number);
            if (year && month && day) {
              onChange({ year, month, day });
            }
          }}
        />
      </label>

      {/* The heading, on the account's clock — which on a late evening is not
          always the same date the viewer's own machine would show. */}
      <p aria-live="polite" className="text-sm font-medium" data-testid="board-heading">
        {view === 'week'
          ? t('dispatch.weekRange', {
              from: formatDate(weekFirstDayStart, { month: 'long', day: 'numeric', timeZone }),
              to: formatDate(weekLastDayStart, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                timeZone,
              }),
            })
          : formatBoardDate(dayStart, timeZone)}
      </p>
    </div>
  );
}
